import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useTemplateManifest } from '../hooks/useTemplateManifest';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getCachedMediaUrl } from '../api/mediaCache';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import FormField from '../components/FormField';
import HelpPopover from '../components/HelpPopover';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

export default function ProductEditor() {
  const { siteId, id } = useParams<{ siteId: string; id: string }>();
  const navigate = useNavigate();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'product');
  const { items: categoryItems, loading: categoriesLoading } = useContent(siteId, 'category');
  const { items: siteItems } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { filterFields } = useTemplateManifest(site?.templateId);

  const isNew = id === 'new';
  const existingItem = isNew
    ? undefined
    : items.find((item) => getContentId(item.contentKey) === id);

  const productSlug = existingItem ? getContentId(existingItem.contentKey) : '';
  const currentPage = productSlug ? `/products/${productSlug}` : '/products';
  const { sendUpdate, sendMediaUpdate } = useEditorPreview(currentPage);

  const siteData = siteItems[0]?.data as Record<string, unknown> | undefined;
  const sectionLabel = (siteData?.sectionLabel as string) || 'Products';
  const singularLabel = sectionLabel.replace(/s$/, '');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const justCreatedRef = useRef(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawContentType = schema?.contentTypes.product;
  const filteredContentType = rawContentType ? filterFields('product', rawContentType) : undefined;
  const fields = filteredContentType?.fields || {};

  const isDirty = justCreatedRef.current
    ? false
    : isNew
      ? Object.values(formData).some((v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
      : JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  const categoryNames = categoryItems
    .map((item) => {
      const data = item.data as Record<string, string>;
      return {
        name: data.name || '',
        sortOrder: parseInt(data.sortOrder || '0', 10) || 0,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map((c) => c.name)
    .filter(Boolean);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  useEffect(() => {
    if (id !== 'new') {
      justCreatedRef.current = false;
    }
  }, [id]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    const imageFields = ['backgroundImage', 'profileImage', 'imageUrl', 'featuredImage', 'logo'];
    if (imageFields.includes(name) && typeof value === 'string' && value) {
      const url = getCachedMediaUrl(value) || value;
      sendMediaUpdate('product', name, url);
    } else {
      sendUpdate('product', name, value);
    }
  };

  const validate = (): boolean => {
    for (const [name, field] of Object.entries(fields)) {
      if (field.required) {
        const val = formData[name];
        if (!val || (typeof val === 'string' && !val.trim())) {
          addToast('error', `${field.label} is required`);
          return false;
        }
        if (field.type === 'images' && Array.isArray(val) && val.length === 0) {
          addToast('error', `${field.label} requires at least one image`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!siteId || !validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        justCreatedRef.current = true;
        const created = await create(formData, items.length);
        addToast('success', `${singularLabel} created`);
        const newId = getContentId(created.contentKey);
        navigate(`/sites/${siteId}/content/products/${newId}`, { replace: true });
      } else if (id) {
        await update(id, formData);
        setOriginalData({ ...formData });
        addToast('success', `${singularLabel} updated`);
      }
    } catch (err) {
      justCreatedRef.current = false;
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || categoriesLoading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (!isNew && !existingItem && !loading) {
    return (
      <div className="page">
        <div className="error-state">
          <p>{singularLabel} not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const help = pageHelp.product;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-back">
          <button
            className="btn-icon"
            onClick={() => navigate(`/sites/${siteId}/content/products`)}
            title={`Back to ${sectionLabel.toLowerCase()}`}
          >
            ←
          </button>
          <h1>{isNew ? `New ${singularLabel}` : `Edit ${singularLabel}`}</h1>
        </div>
        <div className="page-header-actions">
          <HelpPopover title={help.title} description={help.description} tips={help.tips} />
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-form">
        {Object.entries(fields).map(([name, fieldSchema]) => {
          if (name === 'category') {
            return (
              <div key={name} className="form-field">
                <label className="field-label">{fieldSchema.label}</label>
                {categoryNames.length > 0 ? (
                  <select
                    className="field-input field-select"
                    value={(formData.category as string) || ''}
                    onChange={(e) => handleChange('category', e.target.value)}
                  >
                    <option value="">— Uncategorized —</option>
                    {categoryNames.map((catName) => (
                      <option key={catName} value={catName}>
                        {catName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <select className="field-input field-select" disabled>
                      <option>— Uncategorized —</option>
                    </select>
                    <p style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#888' }}>
                      Create categories in the <a href={`/sites/${siteId}/content/categories`}>Categories</a> section first.
                    </p>
                  </div>
                )}
                {fieldSchema.hint && <span className="field-hint">{fieldSchema.hint}</span>}
              </div>
            );
          }

          return (
            <FormField
              key={name}
              name={name}
              schema={fieldSchema}
              value={formData[name]}
              onChange={handleChange}
              siteId={siteId!}
              cmsType="product"
            />
          );
        })}
      </div>
    </div>
  );
}
